import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../lib/api';
import { BATTLE_GENRES, calcBattleTotalCents } from '../data/mockData';
import { VoteCounter } from '../components/VoteCounter';
import type { Battle } from '../types';

function fmtMoney(cents: number) { if(!Number.isFinite(cents))return 'See entry pricing'; return `$${(cents / 100).toFixed(0)}`; }

const STATUS_LABEL: Record<string, string> = {
  UPCOMING: 'Upcoming', SUBMISSIONS_OPEN: 'Registration Open', REVIEWING: 'Reviewing',
  ACTIVE: 'Active', LIVE:'Live', COMPLETED: 'Completed', CANCELLED: 'Cancelled',
};

function formatDate(iso?: string) {
  if (!iso) return 'TBD';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function timeUntil(iso?: string) {
  if (!iso) return 'TBD';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'Closed';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
}

/* ---------- Battle Codes Admin Section ---------- */
function BattleCodesAdmin({ battleId, battleTitle }: { battleId: string; battleTitle: string }) {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const generate = async () => {
    setLoading(true); setErr(null); setMsg(null);
    try {
      const r = await api.generateBattleCodes(battleId);
      setCodes(r.codes || []);
      setMsg(`Generated ${(r.codes || []).length} codes for "${r.battleTitle || battleTitle}".`);
    } catch (e: any) { setErr(e?.detail?.message || e?.message || 'Failed to generate codes'); }
    finally { setLoading(false); }
  };

  const view = async () => {
    setLoading(true); setErr(null); setMsg(null);
    try {
      const r = await api.getBattleCodes(battleId);
      setCodes(r.codes || []);
      setMsg(`Loaded ${(r.codes || []).length} codes.`);
    } catch (e: any) { setErr(e?.detail?.message || e?.message || 'Failed to load codes'); }
    finally { setLoading(false); }
  };

  const goLive = async () => {
    setLoading(true); setErr(null); setMsg(null);
    try {
      await api.goLive(battleId);
      setMsg('Battle is now LIVE! The live video bar is active for all users.');
    } catch (e: any) { setErr(e?.detail?.message || e?.message || 'Failed to go live'); }
    finally { setLoading(false); }
  };

  return (
    <div className="battle-codes-section">
      <div className="battle-codes-header">
        <span className="battle-codes-title">Live Battle Codes</span>
        <div className="battle-codes-actions">
          <button className="btn btn-gold btn-sm" onClick={generate} disabled={loading}>
            {loading ? 'Working…' : 'Generate Battle Codes'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={view} disabled={loading}>
            View Codes
          </button>
          <button className="btn btn-primary btn-sm" onClick={goLive} disabled={loading}>
            Go Live
          </button>
        </div>
      </div>
      {msg && <div className="chip chip-approved" style={{ marginBottom: 6 }}>{msg}</div>}
      {err && <div className="field-hint" style={{ color: 'var(--iam-danger)', marginBottom: 6 }}>{err}</div>}
      {codes.length > 0 && (
        <div className="battle-codes-list">
          {codes.map((c: any, i: number) => (
            <div key={c.id || i} className="battle-code-row">
              <span className="battle-code-entrant">{c.entrantUsername || c.username || `Entrant ${i + 1}`}</span>
              <span className="battle-code-value">{c.code || c.joinCode || '—'}</span>
              <span className={`battle-code-status ${c.used ? 'battle-code-status-used' : 'battle-code-status-unused'}`}>
                {c.used ? 'Used' : 'Unused'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Contestant Join With Code Section ---------- */
function JoinWithCode({ battleId }: { battleId: string }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const join = async () => {
    if (!code.trim()) { setErr('Please enter your battle code.'); return; }
    setLoading(true); setErr(null); setMsg(null);
    try {
      await api.joinWithCode(battleId, code.trim());
      setMsg('Code accepted! Redirecting to live battle…');
      // r may contain a LiveKit token — navigate to the live page
      setTimeout(() => navigate(`/app/live?battleId=${battleId}`), 800);
    } catch (e: any) { setErr(e?.detail?.message || e?.message || 'Invalid or expired code'); }
    finally { setLoading(false); }
  };

  return (
    <div className="join-code-section">
      <div className="join-code-title">Join Live Battle</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Enter the code you received when you entered this battle to join the live video call.
      </div>
      <div className="join-code-input-row">
        <input
          className="input"
          style={{ flex: 1, minWidth: 140 }}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter battle code…"
          maxLength={20}
        />
        <button className="btn btn-gold" onClick={join} disabled={loading || !code.trim()}>
          {loading ? 'Joining…' : 'Join Live Battle'}
        </button>
      </div>
      {msg && <div className="chip chip-approved" style={{ marginTop: 6 }}>{msg}</div>}
      {err && <div className="field-hint" style={{ color: 'var(--iam-danger)', marginTop: 6 }}>{err}</div>}
      <div className="join-code-hint">
        Live battle video calls are for users 18 and older only. By joining, you agree to the community guidelines.
      </div>
    </div>
  );
}

function BattlePricing() {
  const [categories, setCategories] = useState(2);
  const [songsPerCat, setSongsPerCat] = useState(1);
  const safeCats = Math.max(1, categories);
  const safeSongs = Math.max(1, songsPerCat);
  const totalCents = calcBattleTotalCents(Array(safeCats).fill(safeSongs));

  return (
    <div className="card">
      <div className="section-title" style={{ margin: 0 }}>Battle Entry Pricing</div>
      <div className="muted mb-16" style={{ fontSize: 13 }}>
        $50 for your first category entry (includes 1 song). Each extra category is $30. Each extra song within a category is $10.
      </div>
      <div className="price-calc">
        <div className="price-row"><span>First category entry (1 song)</span><span>$50</span></div>
        <div className="price-row"><span>Each additional category</span><span>$30</span></div>
        <div className="price-row"><span>Each additional song (same category)</span><span>$10</span></div>
      </div>
      <div className="divider" />
      <div className="section-title" style={{ margin: 0 }}>Estimate your entry</div>
      <div className="flex gap-12 wrap mb-16" style={{ alignItems: 'center' }}>
        <label className="label" style={{ margin: 0 }}>Categories:</label>
        <input type="number" min={1} max={5} value={categories} onChange={(e) => setCategories(Number(e.target.value))} className="input" style={{ width: 90 }} />
        <label className="label" style={{ margin: 0 }}>Songs per category:</label>
        <input type="number" min={1} max={2} value={songsPerCat} onChange={(e) => setSongsPerCat(Number(e.target.value))} className="input" style={{ width: 90 }} />
      </div>
      <div className="price-calc">
        <div className="price-row"><span>{safeCats} categor{safeCats > 1 ? 'ies' : 'y'} × {safeSongs} song{safeSongs > 1 ? 's' : ''} each</span><span></span></div>
        <div className="price-row" style={{ fontWeight: 700 }}><span>Estimated total</span><span className="price-total">{fmtMoney(totalCents)}</span></div>
      </div>
    </div>
  );
}

function CreateBattleForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('Hip-Hop');
  const [description, setDescription] = useState('');
  const [maxEntrants, setMaxEntrants] = useState(32);
  const [entryDeadline, setEntryDeadline] = useState('');
  const [prize, setPrize] = useState('');
  const [rules, setRules] = useState('Original music only. Lyrics must be original. No unauthorized samples. AI-generated material disclosure required.');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!title.trim()) { setError('Battle name is required.'); return; }
    setSaving(true); setError(null);
    try {
      await api.createBattle({
        title: title.trim(),
        genre,
        description: description.trim(),
        maximumEntrants: maxEntrants,
        entryDeadline: entryDeadline ? new Date(entryDeadline).toISOString() : undefined,
        prize: prize.trim() || undefined,
        rules: rules.trim(),
        status: 'SUBMISSIONS_OPEN',
      });
      onCreated();
      onClose();
    } catch (e: any) {
      setError(e?.detail?.message || e?.message || 'Failed to create battle');
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ gridColumn: '1 / -1', borderColor: 'rgba(201,162,39,0.45)' }}>
      <div className="section-title gold">Create New Battle</div>
      <div className="muted" style={{ fontSize: 14, marginBottom: 12 }}>
        Fill in the details below. Once you click "Save & Publish", the battle will appear in the Arena for all users immediately.
      </div>
      <div className="form-group">
        <label className="label">Battle Name <span className="req">*</span></label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Summer Heat R&B Battle" />
      </div>
      <div className="form-group">
        <label className="label">Category / Genre</label>
        <select className="select" value={genre} onChange={(e) => setGenre(e.target.value)}>
          {BATTLE_GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
          <option value="R&B">R&B</option>
          <option value="Hip-Hop">Hip-Hop</option>
        </select>
      </div>
      <div className="form-group">
        <label className="label">Battle Description</label>
        <textarea className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the battle, who should enter, what kind of music is expected..." />
      </div>
      <div className="grid cols-2">
        <div className="form-group">
          <label className="label">Maximum Entrants</label>
          <select className="select" value={maxEntrants} onChange={(e) => setMaxEntrants(Number(e.target.value))}>
            <option value={24}>24</option>
            <option value={32}>32</option>
            <option value={48}>48</option>
            <option value={64}>64</option>
          </select>
        </div>
        <div className="form-group">
          <label className="label">Entry Deadline</label>
          <input className="input" type="datetime-local" value={entryDeadline} onChange={(e) => setEntryDeadline(e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label className="label">Prize / Rewards</label>
        <input className="input" value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="e.g. Top 3: Champion badge, promotion, timed Contract 4 upgrade" />
      </div>
      <div className="form-group">
        <label className="label">Battle Rules</label>
        <textarea className="textarea" value={rules} onChange={(e) => setRules(e.target.value)} />
      </div>
      <div className="policy-banner" style={{ background: 'rgba(55,184,121,0.08)', borderColor: 'rgba(55,184,121,0.4)', color: 'var(--iam-success)' }}>
        <strong>Entry Requirements:</strong> Original music required · Lyrics must be original · Artist must own rights · No unauthorized samples · AI-generated material disclosure required · Artist profile required
      </div>
      <div className="flex gap-8 mt-16">
        <button className="btn btn-gold" onClick={handleCreate} disabled={saving}>{saving ? 'Publishing…' : 'Save & Publish Battle'}</button>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
      </div>
      {error && <div className="field-hint" style={{ color: 'var(--iam-danger)', marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function BattleContenders({ battleId, isAdmin }: { battleId: string; isAdmin: boolean }) {
  const [entries, setEntries] = useState<any[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.battle(battleId).then((d) => {
      // Get all entries (including waitlist) — admin sees full list
      setEntries(d.standings || []);
    }).catch(() => {});
  }, [battleId]);

  if (!expanded) {
    return (
      <button className="btn btn-outline btn-sm" onClick={() => setExpanded(true)}>
        View Contenders ({entries.length})
      </button>
    );
  }

  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={() => setExpanded(false)}>Hide</button>
      <table className="table" style={{ marginTop: 8 }}>
        <thead><tr><th>#</th><th>Artist</th><th>Votes</th>{isAdmin && <th>Status</th>}</tr></thead>
        <tbody>
          {entries.map((st: any, i: number) => (
            <tr key={st.entryId}>
              <td>{i + 1}</td>
              <td>{st.entrantUsername}</td>
              <td>{st.votes}</td>
              {isAdmin && <td>{st.slotStatus || (i < 32 ? 'COMPETING' : 'WAITLIST')}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BattlesPage() {
  const { currentUser } = useApp();
  const [battles, setBattles] = useState<Battle[]>([]);
  const [selected, setSelected] = useState<Battle | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === 'ADMIN';

  const loadBattles = () => { api.battles().then((d) => setBattles(d.battles)).catch(() => {}); };
  useEffect(() => { loadBattles(); }, []);

  if (selected) return <BattleDetail battle={selected} onBack={() => { setSelected(null); loadBattles(); }} />;

  return (
    <div className="grid">
      <div className="card">
        <div className="section-title">Battle Genres</div>
        <div className="muted mb-16" style={{ fontSize: 13 }}>IAMMUZIK only promotes these genres in battles:</div>
        <div className="flex gap-8 wrap">{BATTLE_GENRES.map((g) => <span key={g} className="chip chip-gold">{g}</span>)}</div>
      </div>

      <BattlePricing />

      <div className="card">
        <div className="muted mb-16" style={{ fontSize: 13 }}>Skill-based music competition. 70% judge score / 30% verified audience vote. No cash prizes — win Tier 4 memberships, badges, and promotion.</div>
        <div className="policy-banner">
          <strong> Explicit Content Notice:</strong> Battle entries may contain explicit content. Live battle video calls are for users 18 and older only.
          <button className="btn btn-gold btn-sm" style={{ marginTop: 8 }} onClick={() => navigate('/app/live')}>
            Join Live Battle →
          </button>
        </div>
      </div>

      {isAdmin && !showCreate && (
        <div className="card" style={{ gridColumn: '1 / -1', borderColor: 'rgba(201,162,39,0.45)' }}>
          <div className="flex-between">
            <div>
              <div className="section-title gold">Admin: Arena Management</div>
              <div className="muted" style={{ fontSize: 13 }}>You see the same Arena as everyone else, plus admin controls. Create battles here and they appear immediately for all users.</div>
            </div>
            <button className="btn btn-gold" onClick={() => setShowCreate(true)}>＋ Create Battle</button>
          </div>
        </div>
      )}

      {isAdmin && showCreate && (
        <CreateBattleForm onClose={() => setShowCreate(false)} onCreated={loadBattles} />
      )}

      {isAdmin && battles.length > 0 && (
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="section-title">Battle Codes & Live Management</div>
          <div className="muted mb-16" style={{ fontSize: 13 }}>
            Generate join codes for contestants, activate the live video bar, and manage live battles.
          </div>
          {battles.map((b) => (
            <div key={b.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: battles.indexOf(b) < battles.length - 1 ? '1px solid var(--iam-border)' : 'none' }}>
              <div className="flex-between mb-8">
                <strong style={{ fontSize: 14 }}>{b.title}</strong>
                <span className="chip chip-gold">{STATUS_LABEL[b.status] || b.status}</span>
              </div>
              <BattleCodesAdmin battleId={b.id} battleTitle={b.title} />
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="section-title">Active Battles</div>
        <div className="grid cols-2">
          {battles.map((b) => (
            <div key={b.id} className="card" style={{ background: 'var(--iam-gray-2)' }}>
              <div className="flex-between gap-8 mb-8">
                <strong style={{ fontSize: 17 }}>{b.title}</strong>
                <span className="chip chip-gold">{STATUS_LABEL[b.status] || b.status}</span>
              </div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>{b.genre} · {b.theme}</div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
                Entry deadline: {timeUntil(b.submissionsCloseAt)} · Max {b.maximumEntrants} entrants
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>Entry fee {fmtMoney(b.entryFeeCents)} — collected at entry (MOCK mode in preview).</div>
              <div className="flex gap-8">
                <button className="btn btn-primary btn-sm" onClick={() => setSelected(b)}>View battle</button>
                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/app/live?battleId=${b.id}`)}>Live Battle</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BattleDetail({ battle, onBack }: { battle: Battle; onBack: () => void }) {
  const { songs, currentUser } = useApp();
  const [detail, setDetail] = useState<any>(null);
  const [songId, setSongId] = useState('');
  const [rightsDeclaration, setRightsDeclaration] = useState(false);
  const [aiDisclosure, setAiDisclosure] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.battle(battle.id).then(setDetail).catch(() => {});
  useEffect(() => { load();const timer=setInterval(load,3000);return()=>clearInterval(timer); }, [battle.id]);

  const isAdmin = currentUser?.role === 'ADMIN';

  const enter = async () => {
    setErr(null); setMsg(null);
    if (!rightsDeclaration) { setErr('You must declare that you own all rights to the submitted material.'); return; }
    if (!aiDisclosure) { setErr('You must disclose whether AI was used in the production of this entry.'); return; }
    try {
      const r = await api.enterBattle(battle.id, songId, rightsDeclaration, aiDisclosure);
      setMsg(`Entered! Position: ${r.entry?.position || '?'}. Status: ${r.entry?.slotStatus || 'COMPETING'}. Payment mode: ${r.mode}.`);
      load();
    } catch (e: any) { setErr(e?.detail?.message || e?.message || 'Could not enter'); }
  };
  const vote = async (entryId: string) => {
    setErr(null); setMsg(null);
    try { await api.vote(battle.id, entryId); setMsg('Vote recorded.'); load(); }
    catch (e: any) { setErr(e?.detail?.message || e?.message || 'Could not vote'); }
  };
  const finalize = async () => {
    setErr(null); setMsg(null);
    try { const r = await api.finalizeBattle(battle.id); setMsg(`Finalized. Top 3: ${r.results.map((x: any) => `#${x.entrantUsername}`).join(', ')}`); load(); }
    catch (e: any) { setErr(e?.detail?.message || e?.message || 'Could not finalize'); }
  };

  battle = detail?.battle || battle;
  const mySongs = songs.filter((s) => s.ownerId === currentUser?.id);
  const eligible = currentUser?.tier === 'TIER_3' || currentUser?.tier === 'TIER_4';
  const adminBlocked = isAdmin;

  return (
    <div className="grid">
      <div className="card">
        <div className="flex-between wrap gap-8">
          <div>
            <button className="btn btn-ghost btn-sm" onClick={onBack}>← All battles</button>
            <h2 style={{ marginTop: 8 }}>{battle.title}</h2>
            <div className="muted">{battle.genre} · {battle.theme}</div>
          </div>
          <span className="chip chip-gold">{STATUS_LABEL[battle.status] || battle.status}</span>
        </div>
        <div className="divider" />
        <div className="grid cols-3">
          <div className="stat"><div className="stat-label">Entry deadline</div><div className="stat-value" style={{ fontSize: 18 }}>{timeUntil(battle.submissionsCloseAt)}</div><div className="muted" style={{ fontSize: 12 }}>{formatDate(battle.submissionsCloseAt)}</div></div>
          <div className="stat"><div className="stat-label">Entry fee</div><div className="stat-value" style={{ fontSize: 18 }}>{fmtMoney(battle.entryFeeCents)}</div></div>
          <div className="stat"><div className="stat-label">Slots</div><div className="stat-value" style={{ fontSize: 18 }}>{battle.maximumEntrants}</div><div className="muted" style={{ fontSize: 12 }}>min {battle.minimumVerifiedEntrants} to start</div></div>
        </div>
        <div className="divider" />
        <h3>Official Rules</h3>
        <p className="muted" style={{ fontSize: 14 }}>{battle.rules}</p>
        <h3>Rewards</h3>
        <p className="muted" style={{ fontSize: 14 }}>{battle.prizeNote}</p>
        <div className="policy-banner" style={{ background: 'rgba(55,184,121,0.08)', borderColor: 'rgba(55,184,121,0.4)', color: 'var(--iam-success)' }}>
          <strong>No cash prizes.</strong> "Promotion" means IAMMUZIK commits to actions — not guaranteed streams, followers, sales, or career outcomes.
        </div>
      </div>

      {battle.status === 'SUBMISSIONS_OPEN' && (
        <div className="card">
          <div className="section-title">Enter this battle</div>
          {adminBlocked ? (
            <div className="field-hint" style={{ color: 'var(--iam-gold)' }}>
              Admin accounts cannot enter battles. This is a server-enforced rule to protect competition integrity.
            </div>
          ) : !eligible ? (
            <div className="field-hint" style={{ color: 'var(--iam-gold)' }}>Battles require Contract 3 or 4. Upgrade on the Contracts page to enter.</div>
          ) : detail?.myEntry ? (
            <div className="chip chip-approved">You're entered — good luck.{detail.myEntry.slotStatus === 'WAITLIST' ? ' You are on the waitlist.' : ''}</div>
          ) : (
            <>
              <div className="form-group">
                <label className="label">Pick your song</label>
                <select className="select" value={songId} onChange={(e) => setSongId(e.target.value)}>
                  <option value="">Select a song…</option>
                  {mySongs.map((s) => <option key={s.id} value={s.id}>{s.title} ({s.genre})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="label">Rights Declaration <span className="req">*</span></label>
                <label className="flex gap-8" style={{ alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={rightsDeclaration} onChange={(e) => setRightsDeclaration(e.target.checked)} />
                  I declare that I own all rights to the submitted material, including lyrics, composition, and any recordings.
                </label>
              </div>
              <div className="form-group">
                <label className="label">AI Disclosure <span className="req">*</span></label>
                <select className="select" value={aiDisclosure} onChange={(e) => setAiDisclosure(e.target.value)}>
                  <option value="">Select disclosure…</option>
                  <option value="NO_AI">No AI tools were used in production</option>
                  <option value="AI_BEAT_ONLY">AI was used to generate the beat/instrumental only</option>
                  <option value="AI_VOICE">AI was used for synthetic voice performance</option>
                  <option value="AI_MIX">AI was used for mixing/mastering</option>
                  <option value="AI_VIDEO">AI was used for video generation</option>
                  <option value="AI_MULTIPLE">AI was used for multiple aspects (beat, voice, video, etc.)</option>
                </select>
              </div>
              <button className="btn btn-gold" onClick={enter} disabled={!songId || !rightsDeclaration || !aiDisclosure}>Enter battle</button>
            </>
          )}
        </div>
      )}

      {/* Contestant Join With Code — shown when battle is active or live */}
      {(battle.status === 'ACTIVE' || battle.status === 'REVIEWING') && !adminBlocked && detail?.myEntry && (
        <JoinWithCode battleId={battle.id} />
      )}

      {detail && ['ACTIVE','LIVE','COMPLETED'].includes(battle.status) && <VoteCounter battleId={battle.id}/>}
      {detail && (['ACTIVE','LIVE','COMPLETED'].includes(battle.status)) && (
        <div className="card">
          <div className="section-title">{battle.status === 'COMPLETED' ? 'Final results' : 'Live standings (votes public, scores sealed)'}</div>
          <table className="table">
            <thead><tr><th>Entrant</th><th>Votes</th>{battle.status === 'COMPLETED' && <th>Total</th>}</tr></thead>
            <tbody>
              {detail.standings?.map((st: any) => (
                <tr key={st.entryId}>
                  <td>{st.entrantUsername}</td>
                  <td>{st.votes}</td>
                  {battle.status === 'COMPLETED' && <td>{st.total}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {['ACTIVE','LIVE'].includes(battle.status) && (
            <div className="flex gap-8 wrap mt-16" style={{ marginTop: 12 }}>
              {detail.standings?.map((st: any) => (
                <button key={st.entryId} className="btn btn-outline btn-sm" disabled={st.entrantUsername === currentUser?.username || !!detail.myVote}
                  onClick={() => vote(st.entryId)}>{detail.myVote ? 'Voted' : `Vote ${st.entrantUsername}`}</button>
              ))}
            </div>
          )}
          {isAdmin && ['ACTIVE','LIVE'].includes(battle.status) && (
            <button className="btn btn-primary mt-16" style={{ marginTop: 12 }} onClick={finalize}>Finalize battle (Admin)</button>
          )}
          <div style={{ marginTop: 12 }}>
            <BattleContenders battleId={battle.id} isAdmin={isAdmin} />
          </div>
        </div>
      )}

      {msg && <div className="chip chip-approved">{msg}</div>}
      {err && <div className="field-hint" style={{ color: 'var(--iam-danger)' }}>{err}</div>}
    </div>
  );
}
